import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import {
  getDemoModeCookie,
  getHotelNameMapping,
} from 'frontend-utils/utils/hash';
import {
  checkIsOwnHotel,
  checkIsOwnHotelOrSmartCompset,
  checkIsPrimaryCompset,
  checkIsSmartCompset,
} from 'lumen/utils/market-insight/market-insight-helpers';

export default class MiHotelsService extends Service {
  @service intl;

  @tracked gridResponseMapping = null;
  @tracked originalGridResponse = null;
  @tracked ownOurHotelId = null;

  get smartCompsetGridResponse() {
    return this.originalGridResponse.filter(checkIsOwnHotelOrSmartCompset);
  }

  static basicValueFormatter(value, useRounding) {
    if (value) {
      if (useRounding) {
        return Math.round(value);
      }
      return value;
    }

    return '--';
  }

  // Will be called when leaving the route/switching hotels
  clearValues() {
    this.originalGridResponse = null;
    this.gridResponseMapping = null;
    this.ownOurHotelId = null;
  }

  /**
   * creates a mapping based on ourHotelId and saves the ourHotelId of the current hotel
   * @param { Array<Object> } hotels : array of objects containing info about hotels
   */
  createMapping(hotels) {
    // Store the original hotels response in the service as well.
    // If not, we will lose this when changing the selected day: the request containing this info is not done a second time, so the default modelResponse will make it an empty array :(

    const hotelsMappingByOurHotelId = {};
    let ownOurHotelId = null;
    hotels.forEach((hotel) => {
      hotel.isOwnHotel = checkIsOwnHotel(hotel);
      if (hotel.isOwnHotel) {
        ownOurHotelId = hotel.our_hotel_id;
      }

      const isPrimaryCompset = checkIsPrimaryCompset(hotel);
      const isSmartCompset = checkIsSmartCompset(hotel);
      // Add an extra "isSmartCompetitor" flag for all smart compset hotels. We only have the extra info about competitors for the smart compset hotels
      // We can't show this for competitors of the primary compset
      if (!hotel.isOwnHotel) {
        if (isPrimaryCompset && isSmartCompset) {
          hotel.compsetClass = 'both';
          hotel.isSmartCompetitor = true;
        } else if (isPrimaryCompset) {
          hotel.compsetClass = 'own-compset';
        } else {
          hotel.compsetClass = 'smart-compset';
          hotel.isSmartCompetitor = true;
        }
      }

      // Handle potential missing data (e.g.: for competitors in the primary compset of the hotel we won't have all data)
      hotel.formattedReviewScore = MiHotelsService.basicValueFormatter(
        hotel.review_score,
      );
      hotel.formattedBasePrice = MiHotelsService.basicValueFormatter(
        hotel.base_price,
        true,
      );

      if (hotel.similarity_score) {
        hotel.formattedSimilarityScore = `${hotel.similarity_score}/5`;
      } else {
        hotel.formattedSimilarityScore = this.intl
          .t('general.missing')
          .toString();
      }

      if (hotel.stars) {
        hotel.formattedStars = [];

        for (let i = 1; i <= hotel.stars; i++) {
          hotel.formattedStars.push('star-filled');
        }
        for (let i = 1; i <= 5 - hotel.stars; i++) {
          hotel.formattedStars.push('star-outline');
        }
      }

      // Anonymize the hotel names when Demo Mode is active
      const demoMode = getDemoModeCookie();
      if (demoMode?.active) {
        if (hotel.isOwnHotel) {
          hotel.name = this.intl.t('reports.own_hotel').toString();
        } else {
          hotel.name = `${this.intl.t('general.hotel.plural', { count: 1 })} ${
            getHotelNameMapping(demoMode, hotel.name).id
          }`;
        }
      }

      hotelsMappingByOurHotelId[hotel.our_hotel_id] = hotel;
    });

    this.originalGridResponse = hotels;
    this.ownOurHotelId = ownOurHotelId;
    this.gridResponseMapping = hotelsMappingByOurHotelId;
  }
}
